import { useMemo, useState, type ReactNode } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  ACTION_CATEGORIES,
  TEAMMATES,
  type ActionCategory,
  type approvalSchema,
  type ConnectionDetail,
  type Decision,
} from "@typedoc/contracts/connections";
import type { z } from "zod";
import { Button, SegmentSwitch, Select, Skeleton, TeammateAvatar } from "@typedoc/ui";
import { ErrorNotice } from "../error-notice";
import { normalizeAppError } from "../../lib/app-error";
import { authClient } from "../../lib/auth-client";
import { decideApproval } from "../../lib/connections/api";
import {
  actionName,
  actorsOf,
  buildDays,
  clock,
  DECISION_WORDS,
  duration,
  isWork,
  whoKey,
  whoName,
  whoOf,
  type Badge,
  type ChangeNode,
  type Chips,
  type Group,
  type Row,
  type Who,
  type WhoKey,
} from "../../lib/connections/activity";
import type { ConnectionsContext } from "../../lib/connections/use-connections-scope";
import {
  approvalsQueryOptions,
  connectionsKey,
  eventsQueryOptions,
} from "../../lib/queries/connections";
import { cx } from "./parts";

type Approval = z.infer<typeof approvalSchema>;

/** The server keeps more than this; past it the log says it is showing the latest. */
const PAGE = 500;

/**
 * Paper "Component · Activity log" (Oct 17, 2026): a connection's history as a short story by
 * day. Work a teammate did wears the teammate's orb and two lines; a change to the account wears
 * a small node and one. A badge appears only when something ended other than asked. An ask still
 * waiting on someone pins above the days and is answered there.
 */
export function ActivityLog({
  connection,
  context,
  onOpenRule,
}: {
  connection: ConnectionDetail;
  context: ConnectionsContext;
  onOpenRule: (category: ActionCategory) => void;
}) {
  const viewerId = context.scope.userId;
  const { data: session } = authClient.useSession();
  const initials = initialsOf(session?.user.name ?? "");
  const events = useQuery(eventsQueryOptions(context, connection.id));
  const approvals = useQuery(approvalsQueryOptions(context));
  const [group, setGroup] = useState<Group>("all");
  const [who, setWho] = useState<WhoKey | null>(null);
  const app = connection.app;

  const all = useMemo(() => events.data?.events ?? [], [events.data]);
  const days = useMemo(
    () => buildDays(all, { viewerId, app, group, who }),
    [all, viewerId, app, group, who],
  );
  const actors = useMemo(() => actorsOf(all, viewerId), [all, viewerId]);
  const pending = (approvals.data?.approvals ?? []).filter(
    (approval) => approval.connectionId === connection.id && approval.status === "pending",
  );

  if (events.error) return <ErrorNotice error={normalizeAppError(events.error)} />;
  if (!events.data) return <Loading />;

  const counts = {
    all: all.filter((event) => !who || whoKey(whoOf(event, viewerId)) === who).length,
    work: 0,
    changes: 0,
  };
  for (const event of all) {
    if (who && whoKey(whoOf(event, viewerId)) !== who) continue;
    if (isWork(event)) counts.work += 1;
    else counts.changes += 1;
  }
  const chosen = actors.find((actor) => whoKey(actor) === who) ?? null;
  const noWork = !all.some(isWork);

  return (
    <div className="activity-log space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <SegmentSwitch
          label="Show"
          value={group}
          options={[
            { value: "all", label: "All", count: counts.all },
            { value: "work", label: "Work", count: counts.work },
            { value: "changes", label: "Changes", count: counts.changes },
          ]}
          onValueChange={setGroup}
        />
        <WhoFilter actors={actors} value={who} onChange={setWho} />
      </div>

      {pending.map((approval) => (
        <PinnedAsk key={approval.id} approval={approval} appSlug={app.slug} context={context} />
      ))}

      <div className="overflow-clip rounded-[10px] border border-border bg-card shadow-[0_1px_2px_#17171a0a]">
        {all.length === 0 ? (
          <p className="m-0 p-4 text-[13px]/5 text-muted-foreground">
            Nothing has happened here yet.
          </p>
        ) : days.length === 0 ? (
          <FilteredEmpty
            who={chosen}
            group={group}
            onReset={() => {
              setWho(null);
              setGroup("all");
            }}
          />
        ) : (
          days.map((day) => (
            <section key={day.key} aria-label={day.date ? `${day.label}, ${day.date}` : day.label}>
              <header className="sticky top-0 z-[2] flex h-10 items-center justify-between gap-3 border-b border-divider bg-band px-4 [section+section_&]:border-t">
                <h3 className="m-0 flex items-baseline gap-2 text-[13px]/[18px] font-semibold">
                  {day.label}
                  {day.date && (
                    <span className="text-xs/4 font-normal text-muted-foreground">{day.date}</span>
                  )}
                </h3>
                <span className="truncate text-xs/4 text-muted-foreground">{day.summary}</span>
              </header>
              <ol className="m-0 list-none p-0">
                {day.rows.map((row, index) => (
                  <LogRow
                    key={row.id}
                    row={row}
                    first={index === 0}
                    last={index === day.rows.length - 1}
                    initials={initials}
                    app={app}
                    onOpenRule={onOpenRule}
                  />
                ))}
              </ol>
            </section>
          ))
        )}
        {days.length > 0 && group !== "changes" && noWork && !who && (
          <p className="m-0 border-t border-divider px-4 py-4 pl-[58px] text-[13px]/5 text-muted-foreground">
            No playbook uses it yet. When one does, each thing a teammate does here lands above,
            newest first.
          </p>
        )}
        {days.length > 0 && (
          <p className="m-0 flex h-11 items-center justify-center border-t border-divider px-4 text-center text-xs/4 text-subtle-foreground">
            {all.length >= PAGE
              ? `Showing the latest ${PAGE} events.`
              : "That's the whole history. Kept for as long as the account is."}
          </p>
        )}
      </div>
    </div>
  );
}

function initialsOf(name: string) {
  const parts = name.trim().split(/\s+/).filter(Boolean);
  const first = parts[0] ?? "";
  const last = parts.at(-1) ?? "";
  return (parts.length > 1 ? first.charAt(0) + last.charAt(0) : first.slice(0, 2))
    .toUpperCase()
    .slice(0, 2);
}

function WhoFilter({
  actors,
  value,
  onChange,
}: {
  actors: Who[];
  value: WhoKey | null;
  onChange: (who: WhoKey | null) => void;
}) {
  const items = [
    { value: "everyone", label: "Everyone" },
    ...actors.map((actor) => ({ value: whoKey(actor), label: whoName(actor) })),
  ];
  return (
    <Select.Root
      size="sm"
      items={items}
      value={value ?? "everyone"}
      onValueChange={(next: string | null) => onChange(!next || next === "everyone" ? null : next)}
    >
      <Select.Trigger aria-label="Who acted" className="w-auto gap-1.5 bg-card">
        <span className="text-muted-foreground">Who</span>
        <Select.Value />
      </Select.Trigger>
      <Select.Content className="workspace-dialog">
        {items.map((item) => (
          <Select.Item key={item.value} value={item.value} label={item.label}>
            <Select.ItemText>{item.label}</Select.ItemText>
          </Select.Item>
        ))}
      </Select.Content>
    </Select.Root>
  );
}

/** Pinned until someone answers: the verb is on the button, the decision lands in its day. */
function PinnedAsk({
  approval,
  appSlug,
  context,
}: {
  approval: Approval;
  appSlug: string;
  context: ConnectionsContext;
}) {
  const queryClient = useQueryClient();
  const action = actionName(approval.tool, appSlug);
  // No teammate asked: the request came from a chat with Abacor.
  const name =
    approval.teammate === null
      ? "Abacor"
      : (TEAMMATES.find((teammate) => teammate.key === approval.teammate)?.name ??
        approval.teammate);
  const decide = useMutation({
    mutationFn: (decision: "approve" | "deny") =>
      decideApproval(context, approval.id, { decision }),
    onSettled: () => queryClient.invalidateQueries({ queryKey: connectionsKey(context.scope) }),
  });
  return (
    <div className="activity-ask rounded-[10px] border px-4 py-3.5 max-sm:pr-3.5">
      <div className="flex flex-wrap items-center gap-x-3.5 gap-y-3">
        <Mark badge="waiting" ring="var(--attention-soft)">
          <TeammateAvatar name={name} size={28} />
        </Mark>
        <div className="min-w-0 flex-1 basis-56">
          <p className="m-0 text-sm/5 text-foreground">
            {name} wants to use {action}
          </p>
          <p className="m-0 mt-0.5 text-xs/4 text-[var(--attention-ink)]">
            {ACTION_CATEGORIES[approval.category].label} asks first · {ago(approval.requestedAt)}
          </p>
        </div>
        <div className="flex shrink-0 gap-1.5">
          <Button
            size="sm"
            variant="outline"
            disabled={decide.isPending}
            onClick={() => decide.mutate("deny")}
          >
            Decline
          </Button>
          <Button size="sm" disabled={decide.isPending} onClick={() => decide.mutate("approve")}>
            Allow
          </Button>
        </div>
      </div>
      {decide.error && <ErrorNotice error={normalizeAppError(decide.error)} className="mt-3" />}
    </div>
  );
}

function ago(iso: string, now = Date.now()) {
  const minutes = Math.round((now - Date.parse(iso)) / 60_000);
  if (minutes < 1) return "just now";
  return `${duration(new Date(now - minutes * 60_000).toISOString(), new Date(now).toISOString())} ago`;
}

/**
 * One line of the story. Lanes: mark 28 · gap 14 · body · time 64. The spine runs through the
 * mark lane and stops at the day's first and last marks. A work row opens in place.
 */
function LogRow({
  row,
  first,
  last,
  initials,
  app,
  onOpenRule,
}: {
  row: Row;
  first: boolean;
  last: boolean;
  initials: string;
  app: { slug: string; name: string };
  onOpenRule: (category: ActionCategory) => void;
}) {
  const [open, setOpen] = useState(false);
  const [showAll, setShowAll] = useState(false);
  const work = row.type === "work";
  // Work marks are 28 and sit 12 down; change nodes are 20 and sit 10 down, centred in the lane.
  const markTop = work ? 12 : 10;
  const time = clock(row.at);

  const mark = <RowMark row={row} initials={initials} />;
  const body = (
    <>
      <div className={cx("min-w-0 flex-1", work ? "pt-3 pb-3.5" : "py-2.5")}>
        <p className={cx("m-0 text-sm/5", work ? "text-foreground" : "text-body-foreground")}>
          {row.subject && <span className="font-medium">{row.subject} </span>}
          {row.text}
          {row.repeats > 1 && (
            <span className="ml-2 inline-flex h-[18px] items-center rounded-full bg-muted px-1.5 align-[1px] font-mono text-[11px]/[14px] text-muted-foreground">
              ×{row.repeats}
            </span>
          )}
        </p>
        <Notes row={row} time={time} showAll={showAll} onShowAll={() => setShowAll(!showAll)} />
        {row.chips && <ChipPair chips={row.chips} />}
      </div>
      <span
        className={cx(
          "min-w-16 shrink-0 text-right font-mono text-[11px]/[14px] whitespace-nowrap text-muted-foreground tabular-nums max-sm:hidden",
          work ? "pt-[15px]" : "pt-[13px]",
        )}
      >
        {open ? clock(row.at, true) : time}
      </span>
    </>
  );

  return (
    <li className="activity-item" data-open={open || undefined}>
      <div className="activity-row">
        {row.openable ? (
          <button
            type="button"
            aria-expanded={open}
            onClick={() => setOpen(!open)}
            className="flex w-full gap-3.5 px-4 text-left"
          >
            <Lane first={first} last={last && !open} top={markTop} open={open}>
              {mark}
            </Lane>
            {body}
          </button>
        ) : (
          <div className="flex gap-3.5 px-4">
            <Lane first={first} last={last} top={markTop}>
              {mark}
            </Lane>
            {body}
          </div>
        )}
        {row.openable && (
          <div className="activity-details" inert={!open}>
            <div>
              <Details row={row} app={app} onOpenRule={onOpenRule} />
            </div>
          </div>
        )}
      </div>
      {row.quiet && showAll && (
        <ol className="m-0 list-none p-0">
          {row.events.map((event) => (
            <li key={event.id} className="flex gap-3.5 px-4 opacity-90">
              <Lane first={false} last={false} top={8}>
                <span className="mt-[11px] block size-1.5 rounded-full bg-input" />
              </Lane>
              <p className="m-0 min-w-0 flex-1 py-2 text-[13px]/5 text-body-foreground">
                {actionName(event.tool, app.slug)}
              </p>
              <span className="w-16 shrink-0 pt-[11px] text-right font-mono text-[11px]/[14px] text-muted-foreground tabular-nums max-sm:hidden">
                {clock(event.at)}
              </span>
            </li>
          ))}
        </ol>
      )}
    </li>
  );
}

function Lane({
  first,
  last,
  top,
  open = false,
  children,
}: {
  first: boolean;
  last: boolean;
  top: number;
  open?: boolean;
  children: ReactNode;
}) {
  return (
    <span className="relative flex w-7 shrink-0 justify-center" style={{ paddingTop: top }}>
      {!(first && last) && !open && (
        <span
          aria-hidden="true"
          className="absolute left-[13px] w-px bg-border"
          style={
            first ? { top, bottom: 0 } : last ? { top: 0, height: top } : { top: 0, bottom: 0 }
          }
        />
      )}
      {children}
    </span>
  );
}

/**
 * The second line: the reason in red when there is one, then the quiet facts, joined by dots. On
 * phones the time lane folds in here and leads the line.
 */
function Notes({
  row,
  time,
  showAll,
  onShowAll,
}: {
  row: Row;
  time: string;
  showAll: boolean;
  onShowAll: () => void;
}) {
  const items: { key: string; node: ReactNode }[] = row.notes.map((note) => ({
    key: note.text,
    node: (
      <span className={note.tone === "danger" ? "text-destructive" : undefined}>{note.text}</span>
    ),
  }));
  if (row.quiet) {
    items.push({
      key: "show",
      node: (
        <button
          type="button"
          className="inline-flex items-center gap-[3px] font-medium text-foreground"
          aria-expanded={showAll}
          onClick={onShowAll}
        >
          {showAll ? "Hide" : `Show all ${row.events.length}`}
          <svg
            width="12"
            height="12"
            viewBox="0 0 12 12"
            aria-hidden="true"
            className={cx("transition-transform duration-fast", showAll && "rotate-180")}
          >
            <path
              d="M3.5 4.75L6 7.25L8.5 4.75"
              fill="none"
              stroke="currentColor"
              strokeWidth="1.3"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        </button>
      ),
    });
  }
  const dot = (
    <span aria-hidden="true" className="text-subtle-foreground">
      ·
    </span>
  );
  return (
    <p
      className={cx(
        "m-0 mt-[3px] flex flex-wrap items-center gap-x-1.5 text-xs/4 text-muted-foreground",
        items.length === 0 && "sm:hidden",
      )}
    >
      <span className="contents sm:hidden">
        <span>{time}</span>
        {items.length > 0 && dot}
      </span>
      {items.map((item, index) => (
        <span key={item.key} className="contents">
          {index > 0 && dot}
          {item.node}
        </span>
      ))}
    </p>
  );
}

const DECISION_CHIP: Record<Decision, string> = {
  always: "bg-[var(--live-soft)] font-medium text-[var(--live)]",
  ask_first: "bg-muted font-medium text-foreground",
  never: "bg-[var(--danger-soft)] font-medium text-destructive",
};

/** Before → after: the old value struck through, the new one in its own color. */
function ChipPair({ chips }: { chips: Chips }) {
  return (
    <p className="m-0 mt-[7px] flex flex-wrap items-center gap-1.5">
      <span className="inline-flex h-5 items-center rounded-[5px] bg-muted px-[7px] text-xs/4 text-muted-foreground line-through decoration-subtle-foreground">
        <span className="sr-only">from </span>
        {chips.from}
      </span>
      <svg width="12" height="12" viewBox="0 0 12 12" aria-hidden="true">
        <path
          d="M2.5 6h7M7 3.5L9.5 6 7 8.5"
          fill="none"
          stroke="var(--muted-foreground)"
          strokeWidth="1.2"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
      </svg>
      <span
        className={cx(
          "inline-flex h-5 items-center rounded-[5px] px-[7px] text-xs/4",
          chips.toDecision ? DECISION_CHIP[chips.toDecision] : "bg-muted text-foreground",
        )}
      >
        <span className="sr-only">to </span>
        {chips.to}
      </span>
    </p>
  );
}

/** What opens: what it was, the rule as it was recorded, how it ended, and what support needs. */
function Details({
  row,
  app,
  onOpenRule,
}: {
  row: Row;
  app: { slug: string; name: string };
  onOpenRule: (category: ActionCategory) => void;
}) {
  const event = row.events[0];
  const category = event.category;
  const [copied, setCopied] = useState(false);
  const result =
    event.kind === "tool_denied"
      ? `Stopped before anything reached ${app.name}.`
      : event.kind === "approval_requested"
        ? "Waiting for an answer."
        : event.kind === "approval_decided"
          ? event.outcome === "approve"
            ? event.detail?.["remember"] === true
              ? "Allowed, and it no longer asks for this action."
              : "Allowed just this once."
            : "Not allowed. Nothing reached the app."
          : event.outcome === "failure"
            ? `${app.name} said it didn't work.`
            : event.outcome === "unknown"
              ? "We never heard how it ended, so it may or may not have happened."
              : "It worked.";
  const reference = event.logId;
  return (
    <div className="mb-1 flex flex-col gap-2.5 border-t border-divider pt-3.5 pr-4 pb-4 pl-[58px]">
      <Fact term="Action">{actionName(event.tool, app.slug)}</Fact>
      {category && (
        <Fact term="Rule">
          <span className="inline-flex flex-wrap items-center gap-1.5">
            {ACTION_CATEGORIES[category].label}
            {event.decision && (
              <span
                className={cx(
                  "inline-flex h-5 items-center rounded-[5px] px-[7px] text-xs/4",
                  DECISION_CHIP[event.decision],
                )}
              >
                {DECISION_WORDS[event.decision]}
              </span>
            )}
            {event.decision && (
              <span className="text-xs/4 text-muted-foreground">as it was then</span>
            )}
          </span>
        </Fact>
      )}
      <Fact term="Result">{result}</Fact>
      {event.tool && (
        <Fact term="For support">
          <span className="inline-flex flex-wrap items-center gap-2">
            <code className="rounded-[4px] border border-divider bg-band px-1.5 py-0.5 font-mono text-[11px]/4 text-body-foreground">
              {event.tool}
            </code>
            {reference && (
              <code className="font-mono text-[11px]/4 text-muted-foreground">
                {reference.length > 14
                  ? `${reference.slice(0, 7)}…${reference.slice(-4)}`
                  : reference}
              </code>
            )}
            <button
              type="button"
              className="inline-flex items-center gap-1 rounded-[4px] px-1 text-[11px]/4 text-muted-foreground hover:text-foreground"
              onClick={() => {
                void navigator.clipboard
                  ?.writeText([event.tool, reference].filter(Boolean).join(" "))
                  .then(() => setCopied(true));
              }}
            >
              <svg width="12" height="12" viewBox="0 0 12 12" aria-hidden="true">
                <rect
                  x="3.75"
                  y="3.75"
                  width="6"
                  height="6"
                  rx="1.2"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="1.1"
                />
                <path
                  d="M2.25 7.5V3.2c0-.5.4-.95.95-.95h4.3"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="1.1"
                  strokeLinecap="round"
                />
              </svg>
              {copied ? "Copied" : "Copy"}
            </button>
          </span>
        </Fact>
      )}
      {category && (
        <div className="pt-1.5">
          <Button size="sm" variant="ghost" onClick={() => onOpenRule(category)}>
            Change the {ACTION_CATEGORIES[category].label} rule
          </Button>
        </div>
      )}
    </div>
  );
}

function Fact({ term, children }: { term: string; children: ReactNode }) {
  return (
    <div className="flex gap-3 max-sm:flex-col max-sm:gap-0.5">
      <span className="w-[84px] shrink-0 text-xs/5 text-muted-foreground">{term}</span>
      <span className="min-w-0 text-[13px]/5 text-foreground">{children}</span>
    </div>
  );
}

/** Who the row is about: a teammate's orb, a person's square, Abacor's mark or a change's node. */
function RowMark({ row, initials }: { row: Row; initials: string }) {
  const who = row.who;
  if (row.type === "work") {
    if (who.kind === "teammate") {
      return (
        <Mark badge={row.badge} stacked={row.quiet}>
          <TeammateAvatar name={who.name} size={28} />
        </Mark>
      );
    }
    return (
      <Mark badge={row.badge} offset={-4}>
        <PersonSquare who={who} initials={initials} />
      </Mark>
    );
  }
  if (who.kind === "abacor") return <AbacorNode />;
  return <Node kind={row.node ?? "list"} />;
}

/**
 * A 28px mark ringed in the card's color, with at most one badge. `stacked` shows a quiet run as
 * a small pile. Badges: amber waiting, red stopped (a bar) or failed (!), green a person's yes.
 */
function Mark({
  children,
  badge,
  stacked = false,
  offset = -3,
  ring = "var(--card)",
}: {
  children: ReactNode;
  badge: Badge;
  stacked?: boolean;
  offset?: number;
  ring?: string;
}) {
  return (
    <span className="relative block size-7 shrink-0">
      {stacked && (
        <span
          aria-hidden="true"
          className="absolute top-[5px] left-0 size-7 rounded-full opacity-60"
          style={{ boxShadow: `0 0 0 3px ${ring}`, background: "var(--muted)" }}
        />
      )}
      <span
        className="relative block size-7 rounded-full"
        style={{ boxShadow: `0 0 0 3px ${ring}` }}
      >
        {children}
      </span>
      {badge && (
        <span
          className={cx(
            "absolute grid size-3.5 place-items-center rounded-full border-2",
            badge === "waiting" && "bg-warning",
            (badge === "stopped" || badge === "failed") && "bg-destructive",
            badge === "yes" && "bg-success",
          )}
          style={{ right: offset, bottom: offset, borderColor: ring }}
        >
          <span className="sr-only">
            {badge === "waiting"
              ? "Waiting on someone"
              : badge === "stopped"
                ? "Stopped by a rule"
                : badge === "failed"
                  ? "Didn't work"
                  : "Allowed"}
          </span>
          {badge === "waiting" && <span className="size-1 rounded-full bg-white" />}
          {badge === "stopped" && <span className="h-[1.5px] w-[5px] rounded-[1px] bg-white" />}
          {badge === "failed" && <span className="text-[8px]/[8px] font-bold text-white">!</span>}
          {badge === "yes" && (
            <svg width="8" height="8" viewBox="0 0 8 8" aria-hidden="true">
              <path
                d="M1.8 4.1L3.3 5.5L6.2 2.6"
                fill="none"
                stroke="#fff"
                strokeWidth="1.3"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          )}
        </span>
      )}
    </span>
  );
}

/** People are rounded squares, so a column of marks says at a glance who was a person. */
function PersonSquare({ who, initials }: { who: Who; initials: string }) {
  const you = who.kind === "you";
  return (
    <span
      className={cx(
        "grid size-7 place-items-center rounded-[11px] border border-[#1c1a170f] text-[11px]/[14px] font-medium",
        you ? "bg-[#ffecc0] text-[#7a5b3a]" : "bg-muted text-body-foreground",
      )}
    >
      {you && initials ? initials : <PersonGlyph />}
    </span>
  );
}

function PersonGlyph() {
  return (
    <svg width="12" height="12" viewBox="0 0 12 12" aria-hidden="true">
      <circle cx="6" cy="4.2" r="2" fill="none" stroke="currentColor" strokeWidth="1.2" />
      <path
        d="M2.5 10.2c0-1.9 1.6-3.3 3.5-3.3s3.5 1.4 3.5 3.3"
        fill="none"
        stroke="currentColor"
        strokeWidth="1.2"
        strokeLinecap="round"
      />
    </svg>
  );
}

function AbacorNode() {
  return (
    <span className="grid size-5 shrink-0 place-items-center rounded-[6px] bg-foreground shadow-[0_0_0_3px_var(--card)]">
      <svg width="10" height="10" viewBox="0 0 10 10" aria-hidden="true">
        <path
          d="M2 5.2L4.1 7.2L8 3"
          fill="none"
          stroke="#fff"
          strokeWidth="1.4"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
      </svg>
    </span>
  );
}

/** A change's 20px node on --muted: plug, pause, people, list, pencil, or a red cross. */
function Node({ kind }: { kind: ChangeNode }) {
  const stroke = kind === "disconnect" ? "var(--destructive)" : "var(--body-foreground)";
  return (
    <span
      className={cx(
        "grid size-5 shrink-0 place-items-center rounded-full shadow-[0_0_0_3px_var(--card)]",
        kind === "disconnect" ? "bg-[var(--danger-soft)]" : "bg-muted",
      )}
    >
      <svg width="12" height="12" viewBox="0 0 12 12" aria-hidden="true">
        {kind === "plug" && (
          <path
            d="M4.5 1.5v2.25M7.5 1.5v2.25M3 3.75h6v1.5a3 3 0 01-6 0v-1.5zM6 8.25v2.25"
            fill="none"
            stroke={stroke}
            strokeWidth="1.2"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        )}
        {kind === "pause" && (
          <>
            <rect x="3.3" y="2.6" width="1.9" height="6.8" rx="0.7" fill={stroke} />
            <rect x="6.8" y="2.6" width="1.9" height="6.8" rx="0.7" fill={stroke} />
          </>
        )}
        {kind === "people" && (
          <>
            <circle cx="4.5" cy="4" r="1.8" fill="none" stroke={stroke} strokeWidth="1.2" />
            <path
              d="M1.5 10c0-1.7 1.3-3 3-3s3 1.3 3 3"
              fill="none"
              stroke={stroke}
              strokeWidth="1.2"
              strokeLinecap="round"
            />
            <path
              d="M8 2.4a1.8 1.8 0 010 3.3M9.2 7.3c.8.5 1.3 1.5 1.3 2.7"
              fill="none"
              stroke={stroke}
              strokeWidth="1.2"
              strokeLinecap="round"
            />
          </>
        )}
        {kind === "list" && (
          <path
            d="M2 3.5h8M2 6h5M2 8.5h6.5"
            fill="none"
            stroke={stroke}
            strokeWidth="1.2"
            strokeLinecap="round"
          />
        )}
        {kind === "rename" && (
          <path
            d="M2.5 9.5l.5-2 4.8-4.8 1.5 1.5L4.5 9 2.5 9.5z"
            fill="none"
            stroke={stroke}
            strokeWidth="1.1"
            strokeLinejoin="round"
          />
        )}
        {kind === "disconnect" && (
          <path
            d="M3.5 3.5l5 5M8.5 3.5l-5 5"
            fill="none"
            stroke={stroke}
            strokeWidth="1.2"
            strokeLinecap="round"
          />
        )}
      </svg>
    </span>
  );
}

/** Filtered to nothing: say whose quiet it is and offer the way back. */
function FilteredEmpty({
  who,
  group,
  onReset,
}: {
  who: Who | null;
  group: Group;
  onReset: () => void;
}) {
  const name = who ? whoName(who) : null;
  const text = name
    ? who?.kind === "teammate"
      ? `${name} hasn't worked in this account.`
      : group === "changes" || who?.kind === "you"
        ? `${name === "You" ? "You haven't" : `${name} hasn't`} ${group === "work" ? "answered anything" : "changed anything"} here.`
        : `Nothing here from ${name}.`
    : group === "work"
      ? "No teammate has worked in this account yet."
      : "Nothing has changed here yet.";
  return (
    <div className="flex items-center justify-between gap-3 bg-background p-4">
      <p className="m-0 text-[13px]/5 text-body-foreground">{text}</p>
      <button
        type="button"
        onClick={onReset}
        className="shrink-0 text-xs/4 font-medium text-foreground underline decoration-input underline-offset-[3px]"
      >
        Show everyone
      </button>
    </div>
  );
}

/** Loading is the shape of the log, not a spinner: a day's band, then work and change rows. */
function Loading() {
  return (
    <output
      aria-label="Loading activity"
      className="block overflow-hidden rounded-[10px] border border-border bg-card"
    >
      <span className="sr-only">Loading activity…</span>
      <span className="flex h-10 items-center justify-between border-b border-divider bg-band px-4">
        <Skeleton className="h-3 w-24" />
        <Skeleton className="h-3 w-28" />
      </span>
      {[62, 48, 70, 40].map((width, index) => {
        const change = index % 2 === 1;
        return (
          <span key={width} className="flex items-center gap-3.5 px-4 py-3">
            <span className="flex w-7 shrink-0 justify-center">
              <Skeleton className={cx("rounded-full", change ? "size-5" : "size-7")} />
            </span>
            <span className="flex flex-1 flex-col gap-[7px]">
              <Skeleton className="h-[9px]" style={{ width: `${width}%` }} />
              {!change && (
                <Skeleton className="h-[7px] rounded-[4px]" style={{ width: `${width - 26}%` }} />
              )}
            </span>
            <Skeleton className="h-[7px] w-11 shrink-0 rounded-[4px]" />
          </span>
        );
      })}
    </output>
  );
}
